import clsx from "clsx";
import { type JSX, FunctionComponent } from "react";

import RSSIcon from "@ui/components/icon/RSSIcon";
import FacebookIcon from "@ui/components/icon/social/FacebookIcon";
import InstagramIcon from "@ui/components/icon/social/InstagramIcon";
import NewsletterIcon from "@ui/components/icon/social/NewsletterIcon";
import ThreadsIcon from "@ui/components/icon/social/ThreadsIcon";
import TikTokIcon from "@ui/components/icon/social/TikTokIcon";
import TwitterIcon from "@ui/components/icon/social/TwitterIcon";
import YoutubeIcon from "@ui/components/icon/social/YoutubeIcon";
import { useComponentConfig } from "@ui/context/component-config-context";
import useDefaults from "@ui/hooks/use-defaults";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";

import * as styles from "./SocialLinks.css";

export type SocialLinkData = {
  facebook?: string | null;
  twitter?: string | null;
  instagram?: string | null;
  youtube?: string | null;
  rss?: string | null;
  tiktok?: string | null;
  threads?: string | null;
  newsletter?: string | null;
};

export interface SocialLinksBaseConfig {
  linkData: SocialLinkData;
}

interface Props extends Partial<SocialLinksBaseConfig> {
  className?: string;
}

const SocialLinks: FunctionComponent<Props> = (props) => {
  const {
    siteSettings: { socialMediaLinks },
  } = useGlobalDataContext();

  const socialLinksConfig = useComponentConfig("socialLinks");
  const analytics = useAnalytics();

  const { className, linkData } = useDefaults(props, socialLinksConfig);

  const handleSocialLinkClick = (name: string) =>
    analytics?.emit({
      event: "navigation_click",
      navigation_click: { name },
    });

  // On WP the platform name is hardcoded by field (it's not an array), so we can rely on it
  const links = [
    {
      key: "facebook",
      content: <FacebookIcon className={styles.socialIcon} />,
      url:
        socialMediaLinks.find((x) => x.platformName === "Facebook")?.url ||
        linkData.facebook,
    },
    {
      key: "twitter",
      content: <TwitterIcon className={styles.socialIcon} />,
      url:
        socialMediaLinks.find((x) => x.platformName === "Twitter")?.url ||
        linkData.twitter,
    },
    {
      key: "threads",
      content: <ThreadsIcon className={styles.socialIcon} />,
      url:
        socialMediaLinks.find((x) => x.platformName === "Threads")?.url ||
        linkData.threads,
    },
    {
      key: "instagram",
      content: <InstagramIcon className={styles.socialIcon} />,
      url:
        socialMediaLinks.find((x) => x.platformName === "Instagram")?.url ||
        linkData.instagram,
    },
    {
      key: "youtube",
      content: <YoutubeIcon className={styles.socialIcon} />,
      url:
        socialMediaLinks.find((x) => x.platformName === "YouTube")?.url ||
        linkData.youtube,
    },
    {
      key: "rss",
      content: <RSSIcon className={styles.socialIcon} />,
      url: linkData.rss,
    },
    {
      key: "tiktok",
      content: <TikTokIcon className={styles.socialIcon} />,
      url:
        socialMediaLinks.find((x) => x.platformName === "TikTok")?.url ||
        linkData.tiktok,
    },
    {
      key: "newsletter",
      content: <NewsletterIcon className={styles.socialIcon} />,
      url: linkData.newsletter,
    },
  ];

  return (
    <ul className={clsx(styles.socialLinksContainer, className)}>
      {links
        .filter(
          (link): link is { key: string; content: JSX.Element; url: string } =>
            !!link.url,
        )
        .map(({ key, content, url }) => (
          <li key={key}>
            <a
              href={url}
              className={clsx(styles.socialIconContainer)}
              onClick={() => handleSocialLinkClick(key)}
              rel="nofollow"
            >
              {content}
            </a>
          </li>
        ))}
    </ul>
  );
};

export default SocialLinks;
